import { PropsWithChildren, createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Theme } from "../models/NavigatorModels";
import { useRouter } from "next/router";
import { useStateRef } from "../hooks/UseStateRef";
import { useFocusScrollIntoView } from "../hooks/useFocusScrollIntoView";

export interface ThemeContext {
	theme: Theme;
	setTheme: (theme: Theme) => void;
	isEmbedded: boolean;
	themeInfo: ThemeInfo;
}

export const ThemeContext = createContext<ThemeContext>({} as ThemeContext);
ThemeContext.displayName = "ThemeContext";

export const COLORS = {
	SHEMED_PINK: "#F06FA0",
	SHEMED_PURPLE: "#60243A",
	HEMED_BLUE: "#213c57",
	BLACK: "#000",
	GRAY: "#E5E5E5",
	GREEN: "#42BF40",
	GREEN_SUCCESS: "#1FAD53",
	YELLOW: "#ECC013"
} as const;

interface ThemeInfo {
	classNames: {
		primary: string;
		button: string;
		buttonForeground: string;
	};
	colors: {
		primary: string;
		success: string;
		buttonColor?: string; // optional, only set if provided via query
	};
	translationSuffix: string;
}

type ThemeOverrides = {
	classNames?: Partial<ThemeInfo["classNames"]>;
	colors?: Partial<ThemeInfo["colors"]>;
};

/** Normalize hex colors like 'fff', '#fff', 'ffffff', '#ffffff' -> '#rrggbb'. Returns undefined if invalid. */
const normalizeHex = (input?: string): string | undefined => {
	if (!input || typeof input !== "string") return undefined;
	let v = input.trim();
	if (v.startsWith("#")) v = v.slice(1);
	if (!/^(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v)) return undefined;
	if (v.length === 3)
		v = v
			.split("")
			.map((ch) => ch + ch)
			.join("");
	return `#${v.toLowerCase()}`;
};

// Case-insensitive check that a string matches a Theme enum value
const parseThemeFromQuery = (val?: string): Theme | undefined => {
	if (!val || typeof val !== "string") return undefined;
	const lower = val.toLowerCase();
	// Theme enum values are already lowercase strings
	return (Object.values(Theme) as string[]).includes(lower) ? (lower as Theme) : undefined;
};

export const ThemeProvider: React.FC<PropsWithChildren> = ({ children }) => {
	const router = useRouter();
	useFocusScrollIntoView();
	// Keep these simple; derive from effects to avoid SSR flicker
	const [theme, _setTheme] = useState<Theme>(Theme.Light);
	const [isEmbedded, setIsEmbedded, isEmbeddedRef] = useStateRef<boolean>(router.query?.embed === "true");
	const [overrides, setOverrides, overridesRef] = useStateRef<ThemeOverrides>({});

	// 🔒 sticky lock when theme comes from query
	const lockRef = useRef<{ locked: boolean; source: "query" | null }>({
		locked: false,
		source: null
	});

	useEffect(() => {
		if (!router.isReady) return;

		let embedded = isEmbeddedRef.current;

		if (typeof window !== "undefined") {
			try {
				if (window.self !== window.top) embedded = true;
			} catch {
				embedded = true;
			}
		}
		if (router.query?.embed === "true") embedded = true;

		setIsEmbedded((prev) => {
			const next = prev || embedded;
			return next;
		});
	}, [router.isReady, router.query?.embed]);

	useEffect(() => {
		if (!router.isReady) return;

		// If already locked by query, never change route-driven theme again
		if (lockRef.current.locked && lockRef.current.source === "query") return;

		const qTheme = parseThemeFromQuery(typeof router.query?.theme === "string" ? router.query.theme : undefined);

		if (qTheme) {
			_setTheme(qTheme);
			lockRef.current = { locked: true, source: "query" };
			return; // explicit query theme wins and locks
		}

		// No query theme; fall back to pid heuristic (non-sticky)
		const pid = typeof router.query?.pid === "string" ? router.query.pid.toLowerCase() : "";
		if (pid) {
			const next = pid.includes(Theme.SheMed) ? Theme.SheMed : pid.includes(Theme.HeMed) ? Theme.HeMed : Theme.Light;
			_setTheme(next);
		}
	}, [router.isReady, router.query?.theme, router.query?.pid]);

	// Parse query-based overrides once query is available
	useEffect(() => {
		const q = router.query;
		if (!q) return;

		const next: ThemeOverrides = {};

		// colors
		const primary = normalizeHex(
			typeof q.primaryColor === "string" ? q.primaryColor : overridesRef.current?.colors?.primary
		);
		const success = normalizeHex(
			typeof q.successColor === "string" ? q.successColor : overridesRef.current?.colors?.success
		);
		const buttonColor = normalizeHex(
			typeof q.buttonColor === "string" ? q.buttonColor : overridesRef.current?.colors?.buttonColor
		);

		if (primary || success || buttonColor) {
			next.colors = {
				...(overridesRef.current?.colors ?? {}),
				...(primary ? { primary } : {}),
				...(success ? { success } : {}),
				...(buttonColor ? { buttonColor } : {})
			};
		}

		// classNames
		const primaryClass = typeof q.primaryClass === "string" ? q.primaryClass.trim() : "";
		const buttonClass = typeof q.button === "string" ? q.button.trim() : "";
		const btnFgRaw =
			typeof q.buttonFg === "string"
				? q.buttonFg
				: typeof q.buttonForeground === "string"
					? q.buttonForeground
					: overridesRef.current?.classNames?.buttonForeground;

		const buttonForeground = typeof btnFgRaw === "string" ? btnFgRaw.trim() : "";

		if (primaryClass || buttonClass || buttonForeground) {
			next.classNames = {
				...(overridesRef.current?.classNames ?? {}),
				...(primaryClass ? { primary: primaryClass } : {}),
				...(buttonClass ? { button: buttonClass } : {}),
				...(buttonForeground ? { buttonForeground } : {})
			};
		}

		// Only update if something actually changed (shallow)
		const prev = overridesRef.current;
		const changed =
			JSON.stringify(prev?.colors ?? {}) !== JSON.stringify(next.colors ?? {}) ||
			JSON.stringify(prev?.classNames ?? {}) !== JSON.stringify(next.classNames ?? {});
		if (changed) setOverrides(next);
	}, [router.query, setOverrides, overridesRef]);

	// Wrap setTheme so external callers can't override a query-locked theme
	const setTheme = useCallback((next: Theme) => {
		if (lockRef.current.locked && lockRef.current.source === "query") {
			return; // ignore overrides once locked by query
		}
		_setTheme(next);
	}, []);

	const themeInfo: ThemeInfo = useMemo(() => {
		const base: ThemeInfo =
			theme === Theme.SheMed
				? {
						classNames: { primary: "shemed-purple", button: "shemed-purple", buttonForeground: "" },
						colors: { primary: COLORS.SHEMED_PURPLE, success: COLORS.SHEMED_PURPLE, buttonColor: COLORS.SHEMED_PURPLE },
						translationSuffix: "-shemed"
					}
				: theme === Theme.HeMed
					? {
							classNames: { primary: "hemed-blue", button: "hemed-blue", buttonForeground: "hemed-white" },
							colors: { primary: COLORS.HEMED_BLUE, success: COLORS.HEMED_BLUE, buttonColor: COLORS.HEMED_BLUE },
							translationSuffix: "-hemed"
						}
					: {
							classNames: { primary: "is-black", button: "", buttonForeground: "" },
							colors: { primary: COLORS.BLACK, success: COLORS.GREEN_SUCCESS, buttonColor: COLORS.BLACK },
							translationSuffix: ""
						};

		return {
			classNames: { ...base.classNames, ...(overrides.classNames ?? {}) },
			colors: { ...base.colors, ...(overrides.colors ?? {}) },
			translationSuffix: base.translationSuffix
		};
	}, [theme, overrides]);

	const value: ThemeContext = useMemo(
		() => ({ theme, setTheme, isEmbedded, themeInfo }),
		[theme, isEmbedded, themeInfo]
	);

	return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
